import type { ComponentType, ReactNode } from "react";

/* ─────────────────────────────────────────────────────────────
   The flat warm system the home page introduced: one white
   surface, orange as the single saturated accent, hairlines
   instead of boxes. Pages using these wrap themselves in
   `.lv-home` (see index.css) so the tokens resolve.
   ───────────────────────────────────────────────────────────── */

/** Section heading. */
export const H2 =
  "text-balance text-[1.5rem] font-extrabold leading-[1.5] tracking-[-0.02em] text-ink-950 sm:text-[1.75rem] md:text-[2rem] md:leading-[1.4]";

/** The paragraph that follows an H2. */
export const LEAD =
  "mt-4 text-pretty text-[15px] leading-[1.95] text-ink-600 sm:text-[16px] sm:leading-[1.9]";

/** Flat solid orange — no gradient, no shadow, no lift on hover. */
export const BTN_PRIMARY =
  "inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-md bg-gerdoo-600 px-7 text-[15px] font-bold leading-none text-white no-underline transition-colors hover:bg-gerdoo-500";

/** The quiet counterpart to BTN_PRIMARY — a text link with a chevron. */
export const LINK_ACCENT =
  "inline-flex items-center gap-1 text-[15px] font-bold text-gerdoo-700 transition-opacity hover:opacity-70";

/** Section wrapper — the system's vertical rhythm and column width. */
export function Band({
  children,
  className = "",
  id,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section id={id} className={`py-24 sm:py-32 ${className}`}>
      <div className="mx-auto max-w-[1160px] px-6 sm:px-10">{children}</div>
    </section>
  );
}

/** Tinted chip holding a lucide icon, used above a sub-heading. */
export function SectionIcon({ icon: Icon }: { icon: ComponentType<{ className?: string }> }) {
  return (
    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-gerdoo-50 text-gerdoo-600 ring-1 ring-inset ring-gerdoo-500/15">
      <Icon className="h-5 w-5" aria-hidden />
    </span>
  );
}

/**
 * A light window frame — title bar with traffic lights, then content.
 * Replaces the dark terminal panels the older pages used.
 */
export function WindowPanel({
  title,
  children,
  className = "",
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`lv-card overflow-hidden p-0 ${className}`}>
      <div className="flex items-center gap-2 border-b border-black/[0.06] bg-gerdoo-50/50 px-5 py-3.5">
        <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
        <span className="ms-3 text-[12px] font-semibold text-ink-500" dir="ltr">
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}
